import type { GateContext, Lesson } from '../types';
import { f, run } from './helpers';

const Q_T = 5;
const medSample = (ctx: GateContext) => ctx.sample('m4-med', Q_T);

export const m4: Lesson = {
  id: 'm4',
  number: 4,
  title: 'Proportional control',
  summary:
    'In this module, you will counter drift with a proportional push. See how proportional control acts like an artificial suspension, and why unexpected cargo always makes the drone settle just short of its mark.',
  objectives: [
    'Calculate the P contribution $K_p e$ with units.',
    'Relate higher $K_p$ to smaller residual error, more oscillation and more saturation.',
    'Explain why P needs some error to hold a constant load.',
  ],
  controls: ['controller-kind', 'Kp', 'feedforward', 'contributions'],
  controllerKinds: ['p'],
  sections: [
    {
      id: 'm4-rule',
      title: 'The proportional rule',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'Rather than slamming motors between extreme on-off limits like bang-bang, practical flight control requires smooth, continuous commands. Proportional (P) control forms the foundational backbone of feedback engineering: calculate a corrective thrust that scales proportionally with the size of the tracking error.',
        },
        {
          type: 'p',
          text: 'The proportional control rule calculates a corrective thrust deviation $u_{fb}$ from the current error $e$:',
        },
        {
          type: 'math',
          tex: '\\term{ufb}{u_{fb}} = \\term{Kp}{K_p}\\, \\term{error}{e} \\qquad \\term{error}{e} = \\term{reference}{r} - \\term{ym}{y_m}',
          aria: 'u f b equals K p times error e, where e is the target r minus the measured altitude y m.',
        },
        {
          type: 'list',
          items: [
            '**Error** ($\\term{error}{e}$): Difference between target altitude $r$ and measured altitude $y_m$. Positive error means the drone is too low.',
            '**Proportional gain** ($\\term{Kp}{K_p}$): Controller gain measured in Newtons per metre (N/m). It specifies how many Newtons of corrective thrust to command for every metre of altitude error.',
          ],
        },
        {
          type: 'p',
          text: 'Units check: $\\text{N/m} \\times \\text{m} = \\text{N}$. For instance, with $K_p = 4\\text{ N/m}$, if the vehicle is $0.25\\text{ m}$ too low ($e = +0.25\\text{ m}$), the controller commands $u_P = 4 \\times 0.25 = +1\\text{ N}$ of extra upward thrust. If it floats $0.25\\text{ m}$ too high ($e = -0.25\\text{ m}$), the controller reduces thrust by $1\\text{ N}$.',
        },
        {
          type: 'callout',
          tone: 'note',
          title: 'Physical intuition: P control acts as an artificial spring',
          text: 'Recall that a physical spring pulls with force $-k\\,q$. A proportional controller pushes with $u_P = K_p e = -K_p q$ when tracking hover altitude $z_0$. In other words, P control acts exactly like an artificial software spring pulling the aircraft toward its target! Adding proportional gain increases the vehicle’s effective stiffness: $\\text{stiffness}_{eff} = k + K_p$.',
        },
      ],
    },
    {
      id: 'm4-metrics',
      title: 'Measuring a response',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'When tuning flight controllers, aerospace engineers cannot rely on qualitative impressions like "it feels responsive." We need rigorous, standardized metrics to quantify flight stability, passenger comfort, and tracking accuracy.',
        },
        {
          type: 'p',
          text: 'Below are the industry-standard transient and steady-state metrics used throughout control engineering. Explore each definition in the interactive widget:',
        },
        { type: 'widget', widget: 'metric-definitions' },
      ],
    },
    {
      id: 'm4-residual',
      title: 'Why some error remains',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'If a package delivery drone picks up a payload or encounters a constant downdraft, you might expect a feedback controller to bring it all the way back to the target altitude. Surprisingly, a pure proportional controller will **always sag** below target under a steady load! Understanding why this offset occurs is one of the most important lessons in control engineering.',
        },
        {
          type: 'p',
          text: 'Consider the physical equilibrium: suppose a steady 1 N downward load hangs from the vehicle. Baseline feedforward $T_0$ only balances the empty vehicle’s weight. To hover motionless, feedback must provide the extra 1 N of upward thrust.',
        },
        {
          type: 'p',
          text: 'However, proportional control only produces force when error is nonzero ($u_P = K_p e$). If the drone were to reach its target altitude perfectly ($e = 0$), proportional thrust would vanish ($u_P = 0$), causing the aircraft to immediately drop! Consequently, the drone must settle at a point where the residual error is just large enough to generate the required holding force.',
        },
        {
          type: 'p',
          text: 'With fixed hover feedforward $T_0$, ideal sensors, and constant disturbance $d$, the settled ({{residual-error|residual}}) error is given by:',
        },
        {
          type: 'math',
          tex: 'e_{steady} = \\frac{\\term{k}{k}\\, \\term{rq}{r_q} - \\term{disturbance}{d}}{\\term{k}{k} + \\term{Kp}{K_p}}',
          aria: 'The steady error equals k times r q minus d, all divided by k plus K p.',
        },
        {
          type: 'p',
          text: 'In our tethered preset ($k = 2\\text{ N/m}$, target at hover $r_q = 0$) under a 1 N downward load ($d = -1\\text{ N}$), a gain of $K_p = 4\\text{ N/m}$ produces a steady sag of $e_{steady} = \\frac{1}{2 + 4} = \\frac{1}{6} \\approx 0.167\\text{ m}$. Increasing $K_p$ shrinks this sag, but can never eliminate it entirely.',
        },
        {
          type: 'callout',
          tone: 'warn',
          title: 'When does P control leave an offset?',
          text: 'In the free-altitude preset ($k = 0$) with no disturbance ($d = 0$), the formula yields zero error, so P control reaches the target altitude cleanly. A steady offset appears whenever an external force demands a holding thrust that feedforward does not supply, such as an unmodeled load, or tether spring tension when the target moves away from $z_s$.',
        },
      ],
    },
    {
      id: 'm4-predict',
      title: 'Predict',
      phase: 'predict',
      blocks: [
        {
          type: 'p',
          text: 'If higher $K_p$ reduces steady-state sag, why don’t engineers simply turn $K_p$ up to infinity? Test your intuition on the trade-offs of high proportional gain.',
        },
        {
          type: 'predict',
          id: 'm4-p1',
          prompt: 'You will try $K_p$ = 4, 16 and 60 N/m. Which will have the smallest residual error, and what might it cost?',
          options: [
            { id: 'low', text: '$K_p$ = 4 will have the smallest error.' },
            { id: 'high', text: '$K_p$ = 60 will have the smallest error, but more oscillation.' },
            { id: 'same', text: 'They will be the same because the load is the same.' },
          ],
          reveal: 'Increasing $K_p$ reduces steady sag ($e_{steady} \\propto 1/(k + K_p)$). However, higher $K_p$ stiffens the system without adding any damping, causing the drone to overshoot and ring with sharp oscillations. Furthermore, multiplying a large error by a huge gain demands massive motor thrust that saturates the rotors ($T \\ge T_{max}$).',
        },
      ],
    },
    {
      id: 'm4-lab',
      title: 'Low, medium, high gain',
      phase: 'experiment',
      blocks: [
        {
          type: 'p',
          text: 'In this lab, you will systematically investigate how proportional gain ($K_p$) alters closed-loop stiffness, disturbance rejection, and transient stability.',
        },
        {
          type: 'h',
          text: 'The Gain Dilemma: Stiffness vs. Stability',
        },
        {
          type: 'p',
          text: '**Purpose**: Proportional control acts as a virtual software spring ($u_P = K_p\\,e$). Increasing $K_p$ stiffens the system against disturbances, reducing steady-state altitude sag under load ($e_{steady} \\propto 1/(k + K_p)$). However, higher gains pump more energy into oscillations without adding damping, resulting in severe overshoot, ringing, and motor saturation ($T \\ge T_{max}$).',
        },
        {
          type: 'p',
          text: 'You will sweep three proportional gains ($K_p = 4, 16,\\text{ and } 60\\text{ N/m}$) across a shared flight profile: an initial hover against a 1 N downward load applied at 1 s, followed by a step climb to 2.5 m at 8 s.',
        },
        { type: 'lab' },
      ],
    },
    {
      id: 'm4-inspect',
      title: 'Inspect',
      phase: 'inspect',
      blocks: [
        {
          type: 'p',
          text: 'Use the telemetry graphs to verify the physical consequences of proportional gain:',
        },
        {
          type: 'list',
          items: [
            'Compare all three runs: check the residual altitude sag just before 8 s against our formula $e_{steady} = 1/(2 + K_p)$. Notice how higher $K_p$ visibly lifts the sag closer to target.',
            'Observe the step response at 8 s: which gain causes the largest overshoot and oscillation? Which one hits motor saturation (shaded on the thrust plot)?',
            'Examine the metrics table: note how high $K_p$ lowers the error integral (IAE) but racks up significant saturation time.',
          ],
        },
      ],
    },
    {
      id: 'm4-explain',
      title: 'Explain',
      phase: 'explain',
      blocks: [
        { type: 'reflect', id: 'm4-r1', prompt: 'If a teammate says to turn $K_p$ way up, what evidence from your runs would you show them?' },
      ],
    },
  ],
  lab: {
    id: 'm4-lab',
    version: 1,
    instructions: 'Complete all three 15 s runs to compare low ($K_p = 4$), medium ($K_p = 16$), and high ($K_p = 60$) proportional gains across a 1 N load disturbance and a 2.5 m climb step.',
    steps: [
      {
        id: 'm4-low',
        title: 'Step 1: Low gain ($K_p = 4$ N/m)',
        presetId: 'p-load',
        setup: (c) => {
          c.controller.Kp = 4;
        },
        instructions:
          'Load the step ($K_p = 4$) and run to the end (15 s). Watch the 1 N load cause a large, permanent altitude sag (~0.17 m) due to weak stiffness, followed by a sluggish climb to 2.5 m at 8 s with zero overshoot.',
        permitted: [],
        requires: 'A complete 15 s run with $K_p$ = 4.',
      },
      {
        id: 'm4-med',
        title: 'Step 2: Medium gain ($K_p = 16$ N/m)',
        presetId: 'p-load',
        setup: (c) => {
          c.controller.Kp = 16;
        },
        instructions:
          'Load the step ($K_p = 16$) and run to the end (15 s). Observe that the load sag shrinks to ~0.05 m; at 8 s, the vehicle climbs briskly to 2.5 m with a moderate overshoot (~0.1 m) and a brief oscillation before settling.',
        permitted: [],
        requires: 'A complete 15 s run with $K_p$ = 16.',
      },
      {
        id: 'm4-high',
        title: 'Step 3: High gain ($K_p = 60$ N/m)',
        presetId: 'p-load',
        setup: (c) => {
          c.controller.Kp = 60;
        },
        instructions:
          'Load the step ($K_p = 60$) and run to the end (15 s). Notice that steady load sag is nearly eliminated, but the 8 s step causes violent overshoot and sustained ringing, while requested thrust slams into the 25 N motor saturation limit.',
        permitted: [],
        requires: 'A complete 15 s run with $K_p$ = 60.',
      },
    ],
  },
  gate: {
    id: 'g4',
    version: 1,
    title: 'Check: proportional control',
    questions: [
      {
        id: 'g4-q1',
        version: 1,
        kind: 'numeric',
        binds: ['m4-med'],
        prompt: (ctx) => `In your $K_p$ = 16 N/m run at t = ${f(medSample(ctx).t)} s, the error was e = ${f(medSample(ctx).e, 4)} m. What was the P contribution $u_P$?`,
        unit: 'N',
        answer: (ctx) => 16 * medSample(ctx).e,
        tolerance: 0.01,
        misconceptions: [
          { test: (x, ctx) => Math.abs(x - 16 / medSample(ctx).e) < 1, feedback: 'Multiply the gain by the error. Do not divide.' },
          { test: (x, ctx) => Math.abs(x + 16 * medSample(ctx).e) < 0.01, feedback: 'Check the sign: positive error (too low) gives an upward (positive) correction.' },
        ],
        hint: '$u_P = K_p e$. Units: N/m × m = N.',
        explanation: 'That upward correction is what holds most of the 1 N load.',
        review: 'm4-rule',
      },
      {
        id: 'g4-q2',
        version: 1,
        kind: 'numeric',
        binds: [],
        prompt: () =>
          'Predict the residual error for $K_p$ = 16 N/m in the tethered preset (k = 2 N/m) with the target at $z_0$ ($r_q$ = 0) and a 1 N downward load (d = −1 N).',
        unit: 'm',
        answer: () => 1 / 18,
        tolerance: 0.002,
        misconceptions: [
          { test: (x) => Math.abs(x - 1 / 16) < 0.002, feedback: 'The spring helps too: the denominator is k + K_p.' },
          { test: (x) => Math.abs(x + 1 / 18) < 0.002, feedback: 'Watch the sign: $-d$ = +1 N, and the vehicle sags below target, so e > 0.' },
        ],
        hint: '$e = (k\\,r_q - d)/(k + K_p)$.',
        explanation: '(0 + 1)/(2 + 16) ≈ 0.056 m. Check it against your run just before 8 s.',
        review: 'm4-residual',
      },
      {
        id: 'g4-q3',
        version: 1,
        kind: 'choice',
        binds: ['m4-low', 'm4-med', 'm4-high'],
        prompt: (ctx) =>
          `Saturation time in your runs: $K_p$ = 4 → ${f(run(ctx, 'm4-low').stats.saturationTime)} s, 16 → ${f(run(ctx, 'm4-med').stats.saturationTime)} s, 60 → ${f(run(ctx, 'm4-high').stats.saturationTime)} s. Which run trades the lowest residual error for the most oscillation and saturation?`,
        options: [
          { id: '4', text: '$K_p$ = 4 N/m', feedback: 'That run has the largest residual error.' },
          { id: '16', text: '$K_p$ = 16 N/m', feedback: 'A middle ground. One run goes further in both directions.' },
          { id: '60', text: '$K_p$ = 60 N/m' },
        ],
        correct: () => '60',
        hint: 'Compare residual error, overshoot after 8 s, and saturation time.',
        explanation: 'The highest gain is the most accurate at steady state but rings and saturates at the step.',
        review: 'm4-inspect',
      },
      {
        id: 'g4-q4',
        version: 1,
        kind: 'choice',
        binds: [],
        prompt: () => 'Why does P control need some nonzero error to hold a constant load?',
        options: [
          { id: 'zero', text: 'Because $u_P = K_p e$. Zero error means zero correction, but the load needs a steady nonzero correction.' },
          { id: 'sensor', text: 'Because the sensor is inaccurate.', feedback: 'These runs use ideal sensing. The error is required by the rule itself.' },
          { id: 'big', text: 'It doesn’t: a large enough $K_p$ makes the error exactly zero.', feedback: 'A larger $K_p$ shrinks the error, but $1/(k + K_p)$ never reaches zero.' },
          { id: 'gravity', text: 'Because gravity is not included.', feedback: 'Gravity is balanced by $T_0$. The unmodelled load is what feedback must carry.' },
        ],
        correct: () => 'zero',
        hint: 'What does the P term output when e = 0?',
        explanation: 'P needs an error to act. Module 6 adds a term that holds a force with zero error.',
        review: 'm4-residual',
      },
    ],
  },
};
